import { css } from '@emotion/css';
import { Tooltip } from 'antd';
import QuickRangePicker, { QuickAction } from 'components/QuickRangePicker';
import dayjs, { Dayjs } from 'lib/dayjs';
import React, { useCallback, useContext, useMemo, useState } from 'react';
import { useTranslate } from '@tolgee/react';
import { last15days, last30days, last7days, last90days, today, yesterday } from '../../utils';
import useIsOU from 'hooks/business/useIsOU';
import { DashboardName } from 'services/api/dashboard';
import { mercadoNewDataDate, mercadoNewDataDays } from './Provider';
import { DashboardFilterCtx } from './useFilter';

/** 快捷日期类型 1：昨日、近7日、近15日 */
export const QUICK_ACTIONS_DEFAULT: QuickAction[] = [
  { key: 'yesterday', label: '昨日', range: [yesterday(), yesterday()] },
  { key: 'last7days', label: '近7日', range: last7days() },
  { key: 'last15days', label: '近15日', range: last15days() },
];

/** 快捷日期类型 2：近7日、近30日 */
export const QUICK_ACTIONS_LAST7_AND_30: QuickAction[] = [
  { key: 'last7days', label: '近7日', range: last7days() },
  { key: 'last30days', label: '近30日', range: last30days() },
];

/** 供上层选择的快捷日期预设 */
export type QuickActionsPreset = 'default' | 'last7And30';

export type { QuickAction };

const PRESET_MAP: Record<QuickActionsPreset, QuickAction[]> = {
  default: QUICK_ACTIONS_DEFAULT,
  last7And30: QUICK_ACTIONS_LAST7_AND_30,
};
const cls = css`
  .ant-picker-cell-disabled {
    pointer-events: auto;
    cursor: not-allowed;
  }
`;
interface ValueType {
  dateRange: [dayjs.Dayjs, dayjs.Dayjs];
}
interface Props<T extends ValueType> {
  value: T;
  onChange: React.Dispatch<React.SetStateAction<T>>;
  max?: Dayjs;
  min?: Dayjs;
  /** 是否显示快捷日期按钮 */
  showQuickActions?: boolean;
  /** 快捷日期预设：default=昨日/近7日/近15日，last7And30=近7日/近30日。仅当 showQuickActions 为 true 时生效 */
  quickActionsPreset?: QuickActionsPreset;
  /** 自定义快捷日期，传入时优先于 quickActionsPreset */
  quickActions?: QuickAction[];
  dashboardName?: DashboardName;
  selectRangeDays?: number;
}

const ExtraNode = <T extends ValueType>(props: Props<T>) => {
  const [minValue, maxValue] = useMemo(() => last90days(), []);

  const {
    value,
    onChange,
    max = maxValue,
    min = minValue,
    showQuickActions = false,
    quickActionsPreset = 'default',
    quickActions: quickActionsProp,
    dashboardName,
    selectRangeDays = 30,
  } = props;
  const { t } = useTranslate();

  const isOU = useIsOU();

  const filterCtx = useContext(DashboardFilterCtx)?.filter;

  const [open, setOpen] = useState(false);

  const [dates, setDates] = useState<[Dayjs, Dayjs] | undefined>();

  const disabledDate = useCallback(
    (date: dayjs.Dayjs) => {
      if (isOU) {
        return false;
      }

      if (!date.isBetween(min, max, 'day', '[]')) {
        return true;
      }

      const isMercadoOrderLoss = dashboardName === 'OrderLoss' && filterCtx?.platform === 'MERCADO';

      if (isMercadoOrderLoss && max.diff(mercadoNewDataDate, 'days') >= mercadoNewDataDays - 1) {
        return date.isBefore(max.subtract(mercadoNewDataDays - 1, 'days'), 'days');
      }

      if (dates) {
        const [start, end] = dates;

        if (isMercadoOrderLoss) {
          const tooLate =
            start && start.isBefore(mercadoNewDataDate, 'days')
              ? date.isSameOrAfter(mercadoNewDataDate, 'days')
              : false;
          const tooEarly =
            end && end.isSameOrAfter(mercadoNewDataDate, 'days')
              ? date.isBefore(mercadoNewDataDate, 'days')
              : false;
          return !!tooEarly || !!tooLate;
        }

        const tooLate = start && date.diff(start, 'days') > selectRangeDays;
        const tooEarly = end && end.diff(date, 'days') > selectRangeDays;

        return !!tooEarly || !!tooLate;
      }
      return false;
    },
    [isOU, min, max, dashboardName, filterCtx?.platform, dates, selectRangeDays],
  );

  const quickActionsWithTrans = useMemo(() => {
    const source = quickActionsProp ?? PRESET_MAP[quickActionsPreset];
    return source.map((x) => ({
      ...x,
      label: t(`ViewDashboard.${x.key}`) as unknown as string,
    }));
  }, [t, quickActionsPreset, quickActionsProp]);

  return (
    <QuickRangePicker
      open={open}
      allowClear={false}
      quickActions={showQuickActions ? quickActionsWithTrans : []}
      value={open ? dates : value.dateRange}
      disabledDate={disabledDate}
      dropdownClassName={cls}
      onCalendarChange={(values) => {
        const [start, end] = values || [];
        if (start && end) {
          onChange((pre) => ({ ...pre, dateRange: [start, end] }));
        }
        setDates([start, end] as any);
      }}
      onChange={(value) => {
        if (!value) {
          return;
        }
        const [start, end] = value;
        let targetEnd: any = end ?? start;
        if (!isOU && end && end.diff(start, 'days') > selectRangeDays) {
          targetEnd = dayjs(start).add(selectRangeDays, 'days');
        }
        if (!isOU && targetEnd.isSameOrAfter(max)) {
          targetEnd = max;
        }
        if (start && targetEnd) {
          onChange((pre) => ({ ...pre, dateRange: [start, targetEnd] }));
        }
      }}
      dateRender={(current) => {
        if (current.isBetween(max, today(), 'day', '(]')) {
          return (
            <Tooltip title={t('ViewDashboard.当前日期数据正在结算中，暂不支持查看。')}>
              <div className="ant-picker-cell-inner">{current.date()}</div>
            </Tooltip>
          );
        }
        return <div className="ant-picker-cell-inner">{current.date()}</div>;
      }}
      onOpenChange={(open) => {
        if (open) {
          setDates(undefined);
        }
        setOpen(open);
      }}
    />
  );
};

export default ExtraNode;
